import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AboutVideo.css';

// "▶ Watch the video" on a community page (community.aboutVideo): a button
// between the title and the tagline for a video the visitor chooses to play,
// with sound and controls — unlike HeroVideo, the silent clip behind the
// hero. Nothing loads until it's tapped. Opens in a full-screen player; Esc,
// the ✕ or tapping outside closes it. Page Analytics counts the tap as
// 'video' (the button's class).
// How far they watched, for Page Analytics: each step once per page visit,
// as 'ic:track' events (hooks/useCommunityPageAnalytics.js records them as
// 'video' actions): played, 25%, 50%, 75%, finished, and seconds watched
// when they close it. Variant C shows the video's first frame in the page
// instead of a button, and tapping it opens the same player (AboutVideoInline).
const track = (label) => {
  try { window.dispatchEvent(new CustomEvent('ic:track', { detail: { type: 'video', label: `Video: ${label}` } })); } catch { /* ignore */ }
};

// Watch tracking for the lightbox. Seconds watched are reported when they
// close it — only when it's grown, as analytics takes the furthest.
const useWatchTracking = () => {
  const reported = useRef(new Set());
  const watchedSec = useRef(0);
  const reportedSec = useRef(0);
  const once = (step) => {
    if (reported.current.has(step)) return;
    reported.current.add(step);
    track(step);
  };
  const onProgress = (e) => {
    const v = e.currentTarget;
    if (!v.duration || !Number.isFinite(v.duration)) return;
    watchedSec.current = Math.max(watchedSec.current, v.currentTime);
    const pct = v.currentTime / v.duration;
    if (pct >= 0.25) once('25%');
    if (pct >= 0.5) once('50%');
    if (pct >= 0.75) once('75%');
  };
  const flush = () => {
    const s = Math.round(watchedSec.current);
    if (s > reportedSec.current) { reportedSec.current = s; track(`watched ${s}s`); }
  };
  return { once, onProgress, flush };
};

// The full-screen player (lightbox): opened by B's button and by tapping
// C's video. Esc, the ✕ or tapping outside closes it; plays with sound
// straight away (started by the tap, so phones allow it).
// smooth: C's version — eases in, sized to the video (no bars), and starts
// playing a second after it opens.
const VideoLightbox = ({ src, poster, title, onClose, smooth = false }) => {
  const videoRef = useRef(null);
  const { once, onProgress, flush } = useWatchTracking();
  const close = () => {
    flush();
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // C's version starts a second after opening, once it has eased in.
    const play = () => videoRef.current?.play?.()?.catch(() => {});
    const timer = smooth ? setTimeout(play, 1000) : null;
    if (!smooth) play();
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drawn straight onto the page (a portal), above everything — inside the
  // hero it was capped by the hero's own layer, so the menu bar sat on top
  // of it and over the ✕ on phones.
  return createPortal(
    <div className={`overlay_aboutVideo${smooth ? ' overlay--smooth_aboutVideo' : ''}`} role="dialog" aria-modal="true" aria-label={title ? `${title} video` : 'Video'} onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      {/* The ✕ sits just above the video's top-right corner. */}
      <div className="frame_aboutVideo">
      <button type="button" className="close_aboutVideo" onClick={close} aria-label="Close video">✕</button>
      <video
        ref={videoRef}
        className="player_aboutVideo"
        src={src}
        poster={poster || undefined}
        controls
        playsInline
        autoPlay={!smooth}
        preload="auto"
        onPlay={() => once('played')}
        onTimeUpdate={onProgress}
        onEnded={() => { once('finished'); }}
      />
      </div>
    </div>,
    document.body
  );
};

const AboutVideo = ({ src, poster, title, label }) => {
  const [open, setOpen] = useState(false);
  if (!src) return null;
  return (
    <>
      <button type="button" className="watch-video-button_aboutVideo" onClick={() => setOpen(true)}>
        <span className="watch-video-icon_aboutVideo" aria-hidden="true">▶</span>
        {label || 'Watch the video'}
      </button>
      {open && <VideoLightbox src={src} poster={poster} title={title} onClose={() => setOpen(false)} />}
    </>
  );
};

export const fmtLength = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

// Variant C of the video test: a still from the video on the first screen,
// under the community title (pages/CommunityHome.js) — never over the hero
// photo — cropped into a landscape hexagon, slightly shaded, with a large
// play button. Tapping it opens the lightbox (VideoLightbox),
// which plays the whole video from the start. Until then only that still
// loads: the frame 6.1 seconds in (#t=6.1), unless there's a poster.
// onLength: its length in seconds, once known.
export const AboutVideoInline = ({ src, poster, title, label, className, onLength }) => {
  const [open, setOpen] = useState(false);

  if (!src) return null;
  return (
    <>
      <div className={`hex-wrap_aboutVideo ${className || ''}`}>
        <div className="hex_aboutVideo">
          <video
            className="inline-player_aboutVideo"
            src={poster ? src : `${src}#t=6.1`}
            poster={poster || undefined}
            aria-hidden="true"
            tabIndex={-1}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) onLength?.(e.currentTarget.duration); }}
          />
          <button type="button" className="inline-cover_aboutVideo" onClick={() => setOpen(true)} aria-label={`Play ${label || 'the video'}`}>
            {/* A big white ▶. */}
            <span className="hex-play_aboutVideo" aria-hidden="true">
              <span className="hex-play-shape_aboutVideo" />
            </span>
          </button>
        </div>
      </div>
      {open && <VideoLightbox src={src} poster={poster} title={title} onClose={() => setOpen(false)} smooth />}
    </>
  );
};

export default AboutVideo;
